import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
} from '@angular/core';
import { TimelogService } from 'src/app/services/timelog.service';
import { SecretService } from './../../../services/secret.service';
import moment from 'moment';

interface DayData {
  date: string;
  day: string;
  billable: number;
  nonBillable: number;
  total: number;
  isToday: boolean;
  isWeekend: boolean;
  requiredHours: number;
}

@Component({
  selector: 'app-time-log-chart',
  templateUrl: './time-log-chart.component.html',
  styleUrls: ['./time-log-chart.component.scss'],
  standalone: false,
})
export class TimeLogChartComponent implements OnInit, OnChanges {
  @Input() userId?: string;
  @Input() month: Date = new Date();
  @Input() requiredHoursPerDay: number = 8;
  @Input() timeLogData: any[] = [];
  @Input() users: any[] = []; // List of users for the dropdown
  @Input() showUserFilter: boolean = true;
  /** Passed to the day's log list: Log time / Edit / Delete buttons. On by default. */
  @Input() showLogTimeButton: boolean = true;
  @Input() showEditButton: boolean = true;
  @Input() showDeleteButton: boolean = true;
  @Output() monthChange = new EventEmitter<Date>();
  @Output() onLogTime = new EventEmitter<void>();
  /** The member picked in the user dropdown (null when cleared). */
  @Output() userChange = new EventEmitter<string | null>();

  chartData: DayData[] = [];
  totalLogged: number = 0;
  totalBillable: number = 0;
  totalNonBillable: number = 0;
  requiredTotal: number = 0;
  maxHours: number = 12;
  yAxisTicks: number[] = [];
  requiredLinePath: string = '';

  selectedUserId: string | null = null;
  loading: boolean = false;

  // Month Navigation
  prevMonth(): void {
    const newDate = new Date(this.month);
    newDate.setMonth(newDate.getMonth() - 1);
    this.monthChange.emit(newDate);
  }

  nextMonth(): void {
    const newDate = new Date(this.month);
    newDate.setMonth(newDate.getMonth() + 1);
    this.monthChange.emit(newDate);
  }

  get isCurrentMonth(): boolean {
    const today = new Date();
    return (
      this.month.getMonth() === today.getMonth() &&
      this.month.getFullYear() === today.getFullYear()
    );
  }

  currMonth(): void {
    if (!this.isCurrentMonth) {
      this.monthChange.emit(new Date());
    }
  }

  constructor(
    private timelogService: TimelogService,
    private secretService: SecretService,
  ) {
    // Initialize with logged-in user if available
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (currentUser && currentUser._id) {
      this.selectedUserId = currentUser._id;
    } else {
      // Fallback: try to get user_id from localStorage and decrypt it
      const userId = this.secretService.decryptLocalStorageData(
        localStorage.getItem('user_id'),
      );
      if (userId) {
        this.selectedUserId = userId;
      }
    }
  }

  ngOnInit(): void {
    // Initial fetch handled by ngOnChanges or constructor?
    // ngOnChanges runs before ngOnInit.
    // If we want to ensure data is fetched even if ngOnChanges didn't trigger data fetch (e.g. no inputs changed?),
    // we can keep it but check if data is already loading.
    // However, month has a default value, but inputs are set by parent.
    // Let's rely on ngOnChanges for 'month' updates.
    // But for the very first load, if 'month' is the default, ngOnChanges might trigger.
    // Actually, to be safe and avoid double calls:
    // We will let ngOnChanges handle it.
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only fetch if month changes.
    // Users list changing doesn't affect chart data.
    // timeLogData is no longer used for fetching.
    if (changes['month'] || changes['userId']) {
      if (changes['userId'] && changes['userId'].currentValue) {
        this.selectedUserId = changes['userId'].currentValue;
      }
      this.fetchChartData();
    }
  }

  onUserSelect(): void {
    this.fetchChartData();
    this.userChange.emit(this.selectedUserId || null);
  }

  fetchChartData(): void {
    this.loading = true;

    // If no user is selected, default to current logged-in user
    let userIdToFetch = this.selectedUserId;
    if (!userIdToFetch) {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      if (currentUser && currentUser._id) {
        userIdToFetch = currentUser._id;
      } else {
        const userId = this.secretService.decryptLocalStorageData(
          localStorage.getItem('user_id'),
        );
        if (userId) {
          userIdToFetch = userId;
        }
      }
    }

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.timelogService
      .getChartData(this.month, userIdToFetch || undefined, timezone)
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.processChartData(response.data);
          }
          this.loading = false;
        },
        error: (err) => {
          console.error('Error fetching chart data:', err);
          this.loading = false;
        },
      });
  }

  processChartData(apiData: any[]): void {
    const startOfMonth = moment(this.month).startOf('month');
    const endOfMonth = moment(this.month).endOf('month');
    const today = moment();
    const daysInMonth = endOfMonth.date();

    this.chartData = [];
    this.totalLogged = 0;
    this.totalBillable = 0;
    this.totalNonBillable = 0;
    this.requiredTotal = 0;

    // Create a map for easy lookup of API data by date
    const dataMap = new Map<string, any>();
    apiData.forEach((item) => {
      dataMap.set(item._id, item);
    });

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = moment(this.month).date(i);
      const dateStr = currentDate.format('YYYY-MM-DD');
      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6; // 0=Sun, 6=Sat

      const dayData = dataMap.get(dateStr) || {
        totalBillable: 0,
        totalNonBillable: 0,
        totalLogged: 0,
      };

      const billable = dayData.totalBillable / 3600;
      const nonBillable = dayData.totalNonBillable / 3600;
      const total = dayData.totalLogged / 3600;

      this.totalLogged += total;
      this.totalBillable += billable;
      this.totalNonBillable += nonBillable;

      // Add required hours logic: 0 on weekends, 8 (or input) on weekdays
      const required = isWeekend ? 0 : this.requiredHoursPerDay;
      this.requiredTotal += required;

      this.chartData.push({
        date: dateStr,
        day: currentDate.format('ddd')[0], // M, T, W...
        billable: parseFloat(billable.toFixed(2)),
        nonBillable: parseFloat(nonBillable.toFixed(2)),
        total: parseFloat(total.toFixed(2)),
        isToday: currentDate.isSame(today, 'day'),
        isWeekend: isWeekend,
        requiredHours: required,
      });
    }

    // Determine Logic for Y-Axis Max and Steps
    const maxData = Math.max(
      ...this.chartData.map((d) => d.total),
      this.requiredHoursPerDay,
    );

    this.generateYAxisTicks(maxData);
    this.generateRequiredLinePath();
  }

  generateYAxisTicks(maxData: number): void {
    if (maxData <= 12) {
      this.maxHours = 12;
      this.yAxisTicks = [12, 10, 8, 6, 4, 2, 0];
    } else {
      // Calculate nice step size for values > 12
      // Target around 5 intervals
      const targetIntervals = 5;
      const step = Math.ceil(maxData / targetIntervals);

      this.maxHours = step * targetIntervals;

      this.yAxisTicks = [];
      for (let i = this.maxHours; i >= 0; i -= step) {
        this.yAxisTicks.push(i);
      }
    }
  }

  generateRequiredLinePath(): void {
    if (this.chartData.length === 0) {
      this.requiredLinePath = '';
      return;
    }

    const points: { x: number; y: number }[] = [];
    const stepX = 100 / this.chartData.length;

    this.chartData.forEach((day, index) => {
      const x = index * stepX + stepX / 2;
      const y = (1 - day.requiredHours / this.maxHours) * 100;
      points.push({ x, y });
    });

    if (points.length === 0) return;

    // Generate smooth path using horizontal bezier (Sigmoid-like) to prevent overshoots
    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      // Midpoint X for the curve control
      const midX = (p1.x + p2.x) / 2;

      // Control points:
      // cp1 is vertically aligned with p1 (y1) but moves horizontally to midX
      // cp2 is vertically aligned with p2 (y2) but starts from midX
      // This creates a perfect S-curve with flat starts/ends
      // However, standard cubic bezier needs 2 control points.
      // C x1 y1, x2 y2, x y

      // To maintain "flatness" at the nodes (tangent = 0), the control points must have the same Y as the anchors.
      // P1 -> CP1(p1.x + smoothing, p1.y) -> CP2(p2.x - smoothing, p2.y) -> P2

      // Let's use 50% of the distance as smoothing handle
      const smoothing = (p2.x - p1.x) * 0.5;

      const cp1x = p1.x + smoothing;
      const cp1y = p1.y;

      const cp2x = p2.x - smoothing;
      const cp2y = p2.y;

      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }

    this.requiredLinePath = d;
  }

  getBarHeight(hours: number): number {
    return (hours / this.maxHours) * 100;
  }

  // Filtering State
  showBillable: boolean = true;
  showNonBillable: boolean = true;

  toggleBillable(): void {
    this.showBillable = !this.showBillable;
  }

  toggleNonBillable(): void {
    this.showNonBillable = !this.showNonBillable;
  }

  getVisibleTotal(day: DayData): number {
    let total = 0;
    if (this.showBillable) total += day.billable;
    if (this.showNonBillable) total += day.nonBillable;
    return parseFloat(total.toFixed(2));
  }

  getPercentageOfRequired(hours: number): string {
    if (!this.requiredHoursPerDay || this.requiredHoursPerDay === 0) return '0';
    return ((hours / this.requiredHoursPerDay) * 100).toFixed(0);
  }

  // Click-to-View Logic
  selectedDate: Date | null = null;
  @Output() onEditLog = new EventEmitter<any>();

  onBarClick(day: DayData): void {
    // If clicking the same date, we can toggle or just keep it.
    // Let's toggle for better UX if they want to hide it.
    if (
      this.selectedDate &&
      moment(this.selectedDate).format('YYYY-MM-DD') === day.date
    ) {
      this.selectedDate = null;
    } else {
      this.selectedDate = new Date(day.date);
    }
  }

  handleLogEdit(log: any): void {
    this.onEditLog.emit(log);
  }

  handleLogTime(): void {
    this.onLogTime.emit();
  }

  refresh(): void {
    this.fetchChartData();
  }
}
